// The F&B menu screen's server state (spec/13 §6.1, F-FNB-01/-02/-03), as TanStack Query hooks over
// the generated client — `packages/contracts/src/routes/food.ts`:
//
//   GET  /v1/organizations/{o}/product-categories        the menu's categories        products:read
//   POST /v1/organizations/{o}/product-categories        add one                      products:manage
//   GET  /v1/organizations/{o}/products                  the products                 products:read
//   POST /v1/organizations/{o}/products                  add one                      products:manage
//   PATCH …/products/{id}/availability                   the org-wide "sold out" flag products:availability
//   PATCH …/venues/{v}/menu/products/{id}/availability   this venue's "sold out"      products:availability
//
// The menu is organisation-scoped (with per-venue overrides, spec/13 §6.1); the venue console reads
// and edits its organisation's menu through the venue's `organizationId`.

import type {
  CreateMenuCategoryRequest,
  CreateMenuProductRequest,
  MenuCategory,
  MenuProduct,
  UpdateMenuCategoryRequest,
  UpdateMenuProductRequest,
} from '@clavian/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../../api/client';
import { sellKeys } from '../sell/queries';

export const menuKeys = {
  categories: (organizationId: string) => ['menu', organizationId, 'categories'] as const,
  products: (organizationId: string) => ['menu', organizationId, 'products'] as const,
};

/** The organisation's menu categories, by sort order. */
export function useMenuCategories(organizationId: string) {
  const api = useApi();
  return useQuery<readonly MenuCategory[]>({
    queryKey: menuKeys.categories(organizationId),
    queryFn: async () => (await api.listMenuCategories({ path: { organizationId } })).categories,
  });
}

/** The organisation's products, with variants and modifiers. */
export function useMenuProducts(organizationId: string) {
  const api = useApi();
  return useQuery<readonly MenuProduct[]>({
    queryKey: menuKeys.products(organizationId),
    queryFn: async () => (await api.listMenuProducts({ path: { organizationId } })).products,
  });
}

/** Add a category, then re-read the list so the new one appears. */
export function useCreateCategory(organizationId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateMenuCategoryRequest) => api.createMenuCategory({ path: { organizationId }, body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: menuKeys.categories(organizationId) }),
  });
}

/** Edit a category (F-FNB-02, `products:manage`); only the fields sent change. Re-reads the list. */
export function useUpdateCategory(organizationId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ categoryId, body }: { categoryId: string; body: UpdateMenuCategoryRequest }) => api.updateMenuCategory({ path: { organizationId, categoryId }, body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: menuKeys.categories(organizationId) }),
  });
}

/** Add a product, then re-read the products so it appears. */
export function useCreateProduct(organizationId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateMenuProductRequest) => api.createMenuProduct({ path: { organizationId }, body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: menuKeys.products(organizationId) }),
  });
}

/** Edit a product (F-FNB-01, `products:manage`); only the fields sent change. Re-read the products after. */
export function useUpdateProduct(organizationId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, body }: { productId: string; body: UpdateMenuProductRequest }) => api.updateMenuProduct({ path: { organizationId, productId }, body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: menuKeys.products(organizationId) }),
  });
}

/**
 * The venue's own "sold out" switch (F-FNB-03): writes this venue's availability, never the product, so the
 * organisation's other venues keep selling it. Re-reads this venue's menu (Sell's too) after.
 */
export function useSetVenueProductAvailability(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, available }: { productId: string; available: boolean }) =>
      api.setVenueProductAvailability({ path: { organizationId, venueId, productId }, body: { available } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: sellKeys.menu(organizationId, venueId) }),
  });
}

/** Toggle a product's availability — the "sold out" switch (F-FNB-03). Re-read the products after. */
export function useSetProductAvailability(organizationId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, available }: { productId: string; available: boolean }) =>
      api.setProductAvailability({ path: { organizationId, productId }, body: { available } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: menuKeys.products(organizationId) }),
  });
}
